import { useMemo, useState, type CSSProperties } from "react";

import {
  AIRSHOW_ASSEMBLY_CANARIES,
  AIRSHOW_ASSEMBLY_FIXTURE_REVISION,
  AIRSHOW_ASSEMBLY_SOURCE_TOOL_VERSION,
  HONDAJET_ASSEMBLY_DEMO,
  buildAssemblyPose,
  type AssemblyPoseNode,
} from "./airshowAssemblySemanticsDemoModel";
import "./airshow-assembly-semantics-demo.css";

const shortRevision = (value: string) => `${value.slice(7, 15)}…${value.slice(-5)}`;

function CanaryTable() {
  return <section className="airshow-assembly-canaries" aria-labelledby="airshow-assembly-canaries-title">
    <header>
      <div><span>Generated source canaries</span><h4 id="airshow-assembly-canaries-title">Importer receipts replayed from native hierarchy evidence</h4></div>
      <code>{shortRevision(AIRSHOW_ASSEMBLY_FIXTURE_REVISION)} · {AIRSHOW_ASSEMBLY_SOURCE_TOOL_VERSION}</code>
    </header>
    <div role="table" aria-label="Airshow assembly canary results">
      <div className="airshow-assembly-canary-head" role="row">
        <span role="columnheader">Aircraft</span><span role="columnheader">Occurrences</span>
        <span role="columnheader">Source edges</span><span role="columnheader">Assembly claim</span>
      </div>
      {AIRSHOW_ASSEMBLY_CANARIES.map((canary) => <article
        data-canary-id={canary.id}
        data-negative-guard={canary.isNegativePartialGuard || undefined}
        data-testid="airshow-assembly-canary"
        key={canary.id}
        role="row"
      >
        <div role="cell"><strong>{canary.label}</strong><code title={canary.archiveMemberPath}>{shortRevision(canary.assemblyReceiptRevision)}</code></div>
        <span role="cell"><b>{canary.occurrenceCount}</b> occurrences</span>
        <span role="cell"><b>{canary.sourceParentEdgeCount}</b> parent edges</span>
        <span role="cell" data-tone={canary.isNegativePartialGuard ? "warn" : "pass"}>
          {canary.isNegativePartialGuard
            ? "Assembly only · semantic partial guard"
            : "Source-bound assembly slice"}
        </span>
      </article>)}
    </div>
  </section>;
}

function HierarchyTree({ nodes }: { nodes: readonly AssemblyPoseNode[] }) {
  return <section className="airshow-assembly-tree-panel">
    <header><span>Loom Aircraft Authoring v2</span><h4>Occurrences + provenance-labelled fixed joints</h4></header>
    <div data-testid="airshow-assembly-tree" role="tree" aria-label="HondaJet imported occurrence hierarchy">
      <div className="airshow-assembly-root" role="treeitem" aria-expanded="true">
        <span><i>◇</i><strong>Aircraft</strong><em>definition</em></span>
      </div>
      {nodes.map((node) => <div
        aria-level={node.depth + 2}
        className="airshow-assembly-tree-row"
        data-motion={node.motion}
        data-occurrence-id={node.occurrenceId}
        data-selected={node.selected}
        data-source-component-id={node.sourceComponentId}
        data-testid={`assembly-node-${node.occurrenceId}`}
        key={node.occurrenceId}
        role="treeitem"
        style={{ "--assembly-depth": node.depth } as CSSProperties}
      >
        <i aria-hidden="true">{node.motion === "moved" ? "↗" : node.selected ? "◎" : "◆"}</i>
        <span><strong>{node.label}</strong><small>{node.semanticKind} · Source {node.sourceComponentId}<br />Loom {node.occurrenceId}</small></span>
        <em>{node.motion === "moved" ? `${node.offsetM.toFixed(2)} m` : node.selected ? "selected · no motion" : "fixed"}</em>
      </div>)}
    </div>
  </section>;
}

function HierarchyDiagram({ nodes }: { nodes: readonly AssemblyPoseNode[] }) {
  const byId = new Map(nodes.map((node) => [node.occurrenceId, node]));
  return <svg
    aria-label="HondaJet occurrence motion diagram"
    className="airshow-assembly-diagram"
    role="img"
    viewBox="0 0 620 340"
  >
    <defs>
      <marker id="airshow-assembly-arrow" markerHeight="6" markerWidth="7" orient="auto" refX="6" refY="3">
        <path d="M0,0 L7,3 L0,6 Z" />
      </marker>
    </defs>
    {nodes.map((node) => {
      const parent = node.parentOccurrenceId ? byId.get(node.parentOccurrenceId) : undefined;
      return parent ? <line
        data-motion={node.motion}
        key={`edge-${node.occurrenceId}`}
        markerEnd="url(#airshow-assembly-arrow)"
        x1={parent.screenX + 34}
        x2={node.screenX - 34}
        y1={parent.screenY}
        y2={node.screenY}
      /> : null;
    })}
    {nodes.map((node) => <g
      data-motion={node.motion}
      data-occurrence-id={node.occurrenceId}
      data-source-component-id={node.sourceComponentId}
      key={node.occurrenceId}
      transform={`translate(${node.screenX} ${node.screenY})`}
    >
      <rect height="38" rx="5" width="104" x="-52" y="-19" />
      <text className="airshow-assembly-node-label" textAnchor="middle" y="-2">{node.label}</text>
      <text className="airshow-assembly-node-kind" textAnchor="middle" y="12">{node.semanticKind}</text>
    </g>)}
  </svg>;
}

function ReceiptPanel({ offsetM }: { offsetM: number }) {
  return <aside className="airshow-assembly-receipt" data-testid="airshow-assembly-receipt">
    <header><span>Import receipt</span><h4>Verified placement evidence · pose preview only</h4></header>
    <dl>
      <div><dt>Transform evidence</dt><dd>OpenVSP post-Update model matrix</dd></div>
      <div><dt>Child-local derivation</dt><dd>inverse(parent-world) × child-world</dd></div>
      <div><dt>Frame + unit binding</dt><dd><code>{shortRevision(HONDAJET_ASSEMBLY_DEMO.frameBindingRevision)}</code></dd></div>
      <div><dt>Topology revision</dt><dd><code>{shortRevision(HONDAJET_ASSEMBLY_DEMO.topologyRevision)}</code></dd></div>
      <div><dt>Generated fixture</dt><dd><code>{shortRevision(AIRSHOW_ASSEMBLY_FIXTURE_REVISION)}</code></dd></div>
      <div><dt>Pose delta</dt><dd>{offsetM.toFixed(2)} m · physical state only</dd></div>
    </dl>
    <div className="airshow-assembly-fail-closed">
      <strong>Fail closed</strong>
      <p>Missing unit, parent, cycle, depth overflow, non-rigid scale/shear, and reflection ambiguity never produce an occurrence tree.</p>
    </div>
  </aside>;
}

export function AirshowAssemblySemanticsDemo() {
  const [offsetM, setOffsetM] = useState(0);
  const pose = useMemo(() => buildAssemblyPose(offsetM), [offsetM]);
  return <section className="airshow-assembly-demo" data-offset-m={offsetM} data-testid="airshow-assembly-demo">
    <header className="airshow-assembly-hero">
      <div>
        <span>Airshow → Loom · assembly semantics vertical slice</span>
        <h3>Move a parent. Keep every other branch still.</h3>
        <p>HondaJet’s native component ancestry becomes stable Loom occurrences joined by fixed joints. The slider applies a bounded pose preview to the imported wing subtree; body-mounted engines and supports remain unchanged.</p>
      </div>
      <div className="airshow-assembly-proof">
        <strong>5 / 5 pinned canaries</strong>
        <span>56 occurrences · 46 source parent edges</span>
        <code>{shortRevision(HONDAJET_ASSEMBLY_DEMO.importReceiptRevision)}</code>
      </div>
    </header>

    <CanaryTable />

    <div className="airshow-assembly-workbench">
      <HierarchyTree nodes={pose.nodes} />
      <section className="airshow-assembly-stage">
        <header>
          <div><span>Honda HA-420 source hierarchy</span><h4>Selected subtree · Wings</h4></div>
          <output htmlFor="airshow-assembly-offset">+{offsetM.toFixed(2)} m</output>
        </header>
        <HierarchyDiagram nodes={pose.nodes} />
        <label className="airshow-assembly-slider" htmlFor="airshow-assembly-offset">
          <span><strong>Wing subtree translation</strong><small>Authoring +Z preview · topology unchanged</small></span>
          <input
            aria-label="Wing subtree translation"
            id="airshow-assembly-offset"
            max="3"
            min="0"
            onChange={(event) => setOffsetM(Number(event.currentTarget.value))}
            step="0.25"
            type="range"
            value={offsetM}
          />
        </label>
        <p aria-live="polite" data-testid="airshow-assembly-pose-status">
          {pose.movedOccurrenceIds.length} moved · {pose.unchangedOccurrenceIds.length} unchanged · topology retained
        </p>
      </section>
      <ReceiptPanel offsetM={offsetM} />
    </div>

    <footer data-testid="airshow-assembly-exactness-boundary">
      <strong>Assembly success does not promote geometry exactness.</strong>
      <span>This is a fixed-joint import and pose preview, not a mechanism solver or full aircraft runtime. Exact / adapted / native-triangle remains a separate body-level decision; the Cessna guard retains remaining propeller, custom-component, and section work.</span>
    </footer>
  </section>;
}

export default AirshowAssemblySemanticsDemo;
